export type GameExitReturnIntent = {
  version: 1;
  userId: string;
  provider: string;
  clientId: string;
  createdAt: string;
};

const prefix = "xh.game.exit-return.v1";

function storageKey(userId: string, provider: string) {
  return `${prefix}.${encodeURIComponent(userId)}.${encodeURIComponent(provider)}`;
}

function validClientId(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{16,80}$/.test(value);
}

function validIntent(value: unknown, userId: string, provider: string): value is GameExitReturnIntent {
  if (!value || typeof value !== "object") return false;
  const intent = value as Partial<GameExitReturnIntent>;
  return intent.version === 1
    && intent.userId === userId
    && intent.provider === provider
    && validClientId(intent.clientId)
    && typeof intent.createdAt === "string"
    && Number.isFinite(Date.parse(intent.createdAt));
}

export function readGameExitReturnIntent(userId: string, provider: string) {
  try {
    const key = storageKey(userId, provider);
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const intent: unknown = JSON.parse(raw);
    if (validIntent(intent, userId, provider)) return intent;
    window.localStorage.removeItem(key);
  } catch { /* Storage may be unavailable; the caller can still use in-memory idempotency. */ }
  return null;
}

export function createGameExitReturnIntent(userId: string, provider: string): GameExitReturnIntent {
  const intent: GameExitReturnIntent = {
    version: 1,
    userId,
    provider,
    clientId: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
  };
  window.localStorage.setItem(storageKey(userId, provider), JSON.stringify(intent));
  const stored = readGameExitReturnIntent(userId, provider);
  if (!stored || stored.clientId !== intent.clientId) throw new Error("Game exit return intent was not persisted");
  return intent;
}

export function persistGameExitReturnIntent(intent: GameExitReturnIntent) {
  window.localStorage.setItem(storageKey(intent.userId, intent.provider), JSON.stringify(intent));
  const stored = readGameExitReturnIntent(intent.userId, intent.provider);
  if (!stored || stored.clientId !== intent.clientId) throw new Error("Game exit return intent was not persisted");
}

export function clearGameExitReturnIntent(intent: GameExitReturnIntent) {
  try {
    const key = storageKey(intent.userId, intent.provider);
    const current = readGameExitReturnIntent(intent.userId, intent.provider);
    if (!current || current.clientId === intent.clientId) window.localStorage.removeItem(key);
  } catch { /* Nothing else to clear. */ }
}
